// Single fetch of the LO Ads Dashboard proxy (ALA-577), shared by HomePage (tab
// visibility) and the Ads page (render). A short in-memory cache dedups the two
// calls so loading the portal and then opening /app/ads does not fan out to the
// three ads-manager reads twice.

export interface AdsFetchResult {
  status: number;
  body: unknown;
}

const CACHE_TTL_MS = 60_000;

let cache: { token: string | null; at: number; result: AdsFetchResult } | null = null;
const inFlightByToken = new Map<string | null, Promise<AdsFetchResult>>();

/**
 * Fetch GET /los/ads/dashboard once, returning { status, body }. A result under
 * CACHE_TTL_MS old is reused; concurrent callers share one in-flight request.
 * Never throws: a network failure resolves to `{ status: 0, body: null }` so
 * callers branch on status (0 and 5xx both mean "unavailable", never "enrolled").
 */
export async function loadAdsDashboard(now: number = Date.now()): Promise<AdsFetchResult> {
  const token = sessionStorage.getItem("ZeitroA");
  if (cache && cache.token === token && now - cache.at < CACHE_TTL_MS) {
    return cache.result;
  }
  const existingRequest = inFlightByToken.get(token);
  if (existingRequest) {
    return existingRequest;
  }
  const request = (async () => {
    let result: AdsFetchResult;
    try {
      const res = await fetch("/los/ads/dashboard", {
        headers: { Authorization: "Bearer " + token },
        cache: "no-store",
      });
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }
      result = { status: res.status, body };
    } catch {
      result = { status: 0, body: null };
    }
    cache = { token, at: now, result };
    return result;
  })();
  inFlightByToken.set(token, request);
  const clearRequest = () => {
    if (inFlightByToken.get(token) === request) inFlightByToken.delete(token);
  };
  request.then(
    clearRequest,
    clearRequest,
  );
  return request;
}

/** Test/logout helper: drop the cached result. */
export function clearAdsCache(): void {
  cache = null;
  inFlightByToken.clear();
}
